import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';

/**
 * A pílula deslizante da barra de abas do módulo jurídico.
 *
 * Vive fora dos componentes porque a MESMA barra existe na ficha do processo e
 * na ficha do cliente. Copiar o bloco garantiria que uma das duas ficasse para
 * trás na próxima mudança — foi assim que quatro telas da Agenda divergiram
 * das cores de atividade antes de existir um arquivo só.
 *
 * A pílula é UM elemento, absoluto, atrás de todos os botões, que se move e se
 * estica até a aba ativa. Como mora DENTRO do container que rola, rolar a
 * barra a carrega junto: sem ouvinte de scroll e sem descolar.
 *
 * Uso:
 *   const scroller = ref(null);
 *   const { estiloPilula, pilulaPronta, prepararPilula, aoRolar } =
 *     useAbaPilula(scroller);
 *
 * O elemento da pílula precisa de `:style="estiloPilula"` e da classe
 * `aba-pilula--viva` quando `pilulaPronta` for verdadeiro. As abas precisam da
 * classe `aba--on` quando ativas — é por ela que a medição encontra o alvo.
 */
export function useAbaPilula(caixaRef, opcoes = {}) {
  // Qual classe marca o botão ativo. O padrão é `.aba--on`, da barra de abas,
  // para as três telas que já usavam este arquivo não mudarem em nada.
  //
  // O parâmetro entrou em 11/09/2026, quando o Victor pediu a mesma pílula
  // deslizante nos SEGMENTADOS de filtro do Financeiro, que usam
  // `.advup-seg__btn--on`. A alternativa era copiar as 300 linhas deste
  // arquivo trocando uma string — e copiar é exatamente como a barra de abas
  // de quatro telas da Agenda divergiu antes de este arquivo existir.
  const seletorAtivo = opcoes.seletorAtivo || '.aba--on';
  const pilulaAba = ref({ x: 0, y: 0, w: 0, h: 0 });
  const pilulaPronta = ref(false);
  const podeRolarEsquerda = ref(false);
  const podeRolarDireita = ref(false);

  // Quando a medição sai zerada, isto guarda que ela precisa ser refeita.
  // Ver o comentário de `medirPilula`.
  const medicaoFalhou = ref(false);

  const medirPilula = () => {
    const caixa = caixaRef.value;
    const botao = caixa?.querySelector(seletorAtivo);
    // Sem barra na tela não há o que medir — mas FICA MARCADO que a medição
    // continua devendo. Sem esta marca, quem tentasse remedir depois não teria
    // como saber que a pílula ainda está zerada. Ver `observarACaixa`.
    if (!botao) {
      medicaoFalhou.value = true;
      return;
    }
    // Medido por `getBoundingClientRect`, e não por `offsetLeft/offsetWidth`:
    // offset arredonda para inteiro e a pílula saía ~2px mais estreita que a
    // aba, deixando uma fresta de fundo aparecendo na borda.
    // Somar o scroll converte de coordenada de tela para coordenada de
    // conteúdo, que é o sistema em que a pílula é posicionada.
    const r = botao.getBoundingClientRect();
    const rc = caixa.getBoundingClientRect();
    // 🔴 ABA DE NAVEGADOR OCULTA MEDE ZERO (medido em 10/09/2026).
    //
    // O `armarPilula` abaixo já tratava o `requestAnimationFrame`, que não roda
    // em aba de fundo. Mas a rede era só para LIGAR a pílula; a MEDIÇÃO ficou
    // de fora, e é ela que quebra: em aba oculta o Chrome não faz layout, então
    // `getBoundingClientRect` devolve zeros. A pílula nascia com `width: 0` e
    // ficava assim para sempre.
    //
    // O sintoma não parecia ser esse: a aba ativa tem TEXTO BRANCO, contando
    // com o fundo azul da pílula atrás. Sem a pílula, é branco no branco — o
    // "menu todo em branco apagado" que o Victor apontou. E aparecia justamente
    // para quem abre pasta em aba de fundo, que é o caminho normal desde que o
    // botão direito passou a funcionar.
    //
    // Zero com botão na tela é medição inválida, não posição: guarda para
    // refazer quando a aba aparecer, em vez de gravar o zero.
    if (r.width === 0) {
      medicaoFalhou.value = true;
      return;
    }

    medicaoFalhou.value = false;
    pilulaAba.value = {
      x: r.left - rc.left + caixa.scrollLeft,
      y: r.top - rc.top + caixa.scrollTop,
      w: r.width,
      h: r.height,
    };
  };

  // `requestAnimationFrame` NÃO roda enquanto a aba do navegador está oculta —
  // e foi exatamente isso que deixou a pílula invisível no primeiro teste na
  // ficha do processo. Quem abre numa aba de fundo cairia no mesmo buraco.
  // O `setTimeout` é a rede: se o rAF veio, o segundo disparo não muda nada.
  const armarPilula = () => {
    const ligar = () => {
      pilulaPronta.value = true;
    };
    requestAnimationFrame(ligar);
    setTimeout(ligar, 80);
  };

  const estiloPilula = computed(() => ({
    transform: `translate3d(${pilulaAba.value.x}px, ${pilulaAba.value.y}px, 0)`,
    width: `${pilulaAba.value.w}px`,
    height: `${pilulaAba.value.h}px`,
  }));

  const aoRolar = () => {
    medirPilula();
    const el = caixaRef.value;
    if (!el) return;
    podeRolarEsquerda.value = el.scrollLeft > 1;
    // -1 absorve o arredondamento sub-pixel do zoom do navegador, senão a
    // seta da direita fica acesa para sempre mesmo no fim da rolagem.
    podeRolarDireita.value =
      el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  };

  /**
   * Chamar ao TROCAR de aba, no `@click`.
   *
   * DEFEITO que isto existe para evitar: `aoRolar()` mede de forma síncrona, e
   * no `@click` ela roda ANTES de o Vue mover a classe `aba--on` para a aba
   * nova. A pílula media a aba ANTERIOR e parava debaixo dela — ficava uma aba
   * defasada, com o texto escuro por cima do azul. Aconteceu em três telas de
   * uma vez (detalhe do cartão do Pipeline, barra de visões e chat interno),
   * porque o `@click` é o lugar óbvio para chamar.
   *
   * O `nextTick` espera o Vue aplicar a classe; só então mede.
   */
  const aoTrocarAba = async () => {
    await nextTick();
    aoRolar();
  };

  // TRAZ A ABA ESCOLHIDA PARA DENTRO DA BARRA (07/10/2026). As fichas
  // repetiam o mesmo `scrollIntoView({ inline: 'nearest' })` cada uma, e no
  // celular ele não fazia nada: a barra tem encaixe de rolagem (`scroll-snap`,
  // a aba encaixa pelo INÍCIO), e "o mais perto" de uma aba larga cortada
  // na borda é o próprio lugar onde ela já está — medido: `scrollLeft` preso
  // em 916 com "Documentos 3" saindo da barra em 390px. Alinhar pelo início
  // É um ponto de encaixe; a folga das setas vem do `scroll-padding-inline`
  // de `.aba-caixa`. No computador (sem encaixe) segue o "mais perto".
  const trazerAbaAtiva = async () => {
    await nextTick();
    const caixa = caixaRef.value;
    const aba = caixa?.querySelector(seletorAtivo);
    if (!caixa || !aba) return;
    const rc = caixa.getBoundingClientRect();
    const ra = aba.getBoundingClientRect();
    const folga =
      parseFloat(getComputedStyle(caixa).scrollPaddingInlineStart) || 0;
    const inteira =
      ra.left >= rc.left + folga - 1 && ra.right <= rc.right - folga + 1;
    if (!inteira) {
      const comEncaixe = getComputedStyle(caixa).scrollSnapType !== 'none';
      aba.scrollIntoView({
        block: 'nearest',
        inline: comEncaixe ? 'start' : 'nearest',
      });
    }
    aoRolar();
  };

  // Posiciona sem transição e só então liga a animação. Para toda vez que a
  // barra pode ter mudado de tamanho SEM ninguém ter clicado: aí a pílula deve
  // corrigir a posição em silêncio, não deslizar sozinha.
  const prepararPilula = async () => {
    pilulaPronta.value = false;
    await nextTick();
    medirPilula();
    // As setas TAMBÉM são recalculadas aqui, e não só na montagem: numa ficha
    // que carrega o registro DEPOIS de montar, `onMounted` roda com a barra
    // ainda inexistente, `aoRolar` sai cedo e as duas setas ficam apagadas
    // para sempre — com a barra cortando abas. Medido na ficha do cliente:
    // 1815px de conteúdo em 1662px de caixa, e nenhuma seta.
    aoRolar();
    armarPilula();
  };

  // Refaz a medição quando a aba do navegador volta a ficar visível — é o
  // primeiro instante em que o layout existe de verdade. Só age se a medição
  // anterior falhou, para não deslizar a pílula sem motivo a cada troca de aba
  // do navegador. Fica DEPOIS de `prepararPilula` porque a chama.
  const aoVoltarAVer = () => {
    if (document.visibilityState !== 'visible' || !medicaoFalhou.value) return;
    prepararPilula();
  };

  // ── 🔴 A CAUSA DE VERDADE DO "MENU EM BRANCO" (medido em 10/09/2026) ──
  //
  // A barra de abas mora dentro de um `v-else-if` que só rende DEPOIS de o
  // processo carregar. Todas as chamadas automáticas de medição acontecem
  // antes disso:
  //   - `onMounted` roda com a barra ainda inexistente;
  //   - o watch do `id` do processo dispara quando o registro chega, mas a
  //     tela ainda está no galho "carregando" — a barra aparece um tique
  //     depois, quando `loadingProcess` vira falso, que é OUTRA mudança;
  //   - e nada remedia depois.
  //
  // Resultado: a pílula ficava com o `{w:0,h:0}` inicial. E como a aba ativa
  // tem texto BRANCO contando com o fundo azul dela, dava branco no branco —
  // o "menu todo em branco apagado". Provado na tela: clicar numa aba media
  // 158px na hora, porque aí a barra existe.
  //
  // Em vez de tentar adivinhar o instante certo (foi o que falhou três vezes),
  // OBSERVA a barra: o `ResizeObserver` avisa quando ela nasce e a cada vez que
  // muda de tamanho. Isso cobre a ordem de renderização, a fonte que carrega
  // depois, o contador de aba que chega atrasado e o redimensionar da janela —
  // com um mecanismo só, e sem depender de quem chama o quê primeiro.
  let vigia = null;
  const observarACaixa = elemento => {
    vigia?.disconnect();
    vigia = null;
    if (!elemento || typeof ResizeObserver === 'undefined') return;
    vigia = new ResizeObserver(() => {
      // `aoRolar` mede a pílula E recalcula as setas: as duas coisas dependem
      // do tamanho da barra, que é justamente o que mudou.
      aoRolar();
      if (!pilulaPronta.value) armarPilula();
    });
    vigia.observe(elemento);
  };

  watch(caixaRef, elemento => observarACaixa(elemento), { immediate: true });

  const rolar = passo => {
    const el = caixaRef.value;
    if (!el) return;
    el.scrollBy({
      left: passo * Math.round(el.clientWidth * 0.7),
      behavior: 'smooth',
    });
  };

  // ── Deslizar a barra na horizontal ──────────────────────────────────────
  //
  // As setas resolviam só metade: quem está de mouse comum não tinha como
  // rolar sem mirar num alvo de 32px, e a roda do mouse sobre a barra rolava
  // a PÁGINA — a barra ficava parada enquanto a tela inteira andava.
  //
  // Duas entradas a mais, as duas do mesmo gesto que se espera de uma régua
  // que não cabe: girar a roda em cima dela, e arrastar.

  const arrasto = ref(null);
  const arrastando = ref(false);

  // Roda vertical vira rolagem horizontal. Só quando HÁ corte: sem isso a
  // barra engoliria a rolagem da página numa tela em que tudo já cabe.
  // `deltaX` maior que `deltaY` é trackpad na horizontal, que o navegador já
  // trata sozinho — não mexer.
  const aoRoda = evento => {
    const el = caixaRef.value;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    if (Math.abs(evento.deltaX) > Math.abs(evento.deltaY)) return;
    evento.preventDefault();
    el.scrollLeft += evento.deltaY;
    aoRolar();
  };

  const aoPegar = evento => {
    const el = caixaRef.value;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    // Só o botão principal, e nunca com um modificador: `ctrl`+arrastar e o
    // botão do meio são gestos do sistema.
    if (evento.button !== 0) return;
    arrasto.value = {
      x: evento.clientX,
      inicio: el.scrollLeft,
      andou: false,
      alvo: evento.currentTarget,
    };
  };

  const aoArrastar = evento => {
    const el = caixaRef.value;
    if (!el || !arrasto.value) return;
    const distancia = evento.clientX - arrasto.value.x;
    // 4px de folga: sem eles um clique com a mão trêmula viraria arrasto, e a
    // aba não trocaria.
    if (!arrasto.value.andou && Math.abs(distancia) < 4) return;
    if (!arrasto.value.andou) {
      arrasto.value.andou = true;
      arrastando.value = true;
      // Segurar o ponteiro: sem isto, soltar o botão fora da barra deixa o
      // arrasto preso e a barra continua andando com o mouse.
      arrasto.value.alvo?.setPointerCapture?.(evento.pointerId);
    }
    el.scrollLeft = arrasto.value.inicio - distancia;
    aoRolar();
  };

  const engolirClique = evento => {
    evento.stopPropagation();
    evento.preventDefault();
  };

  const aoSoltar = evento => {
    if (!arrasto.value) return;
    const andou = arrasto.value.andou;
    arrasto.value.alvo?.releasePointerCapture?.(evento.pointerId);
    arrasto.value = null;
    // O `pointerup` de um arrasto é seguido de um `click` na aba que estava
    // sob o dedo — trocar de aba porque a pessoa quis EMPURRAR a barra é
    // resposta errada. Este ouvinte de uma vez só engole esse clique.
    if (andou) {
      window.addEventListener('click', engolirClique, {
        capture: true,
        once: true,
      });
      setTimeout(() => {
        arrastando.value = false;
      }, 0);
    }
  };

  // A barra pode caber ou não conforme a largura da janela — recalcula na
  // montagem e a cada resize, senão a seta fica acesa (ou some) fora de hora.
  onMounted(async () => {
    await nextTick();
    aoRolar();
    window.addEventListener('resize', aoRolar);
    document.addEventListener('visibilitychange', aoVoltarAVer);
    prepararPilula();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', aoRolar);
    document.removeEventListener('visibilitychange', aoVoltarAVer);
    vigia?.disconnect();
  });

  return {
    estiloPilula,
    pilulaPronta,
    podeRolarEsquerda,
    podeRolarDireita,
    arrastando,
    prepararPilula,
    medirPilula,
    aoRolar,
    aoTrocarAba,
    trazerAbaAtiva,
    rolar,
    aoRoda,
    aoPegar,
    aoArrastar,
    aoSoltar,
  };
}
